import { QueryView, WithTooltip } from "@simplicity/ui";
import { describeScheduleStatus, useScheduleStatus, type ScheduleStatus } from "./scheduleStatus";

const LINE = "mt-1 block text-xs";

function StatusLine({ status, nowMs }: { status: ScheduleStatus | undefined; nowMs: number }) {
  // No entry: the scheduler has nothing to resolve on this schedule (no tables, a
  // period it does not know, a paused connector). Saying nothing beats guessing why.
  if (!status) return null;
  const view = describeScheduleStatus(status, nowMs);

  if (view.kind === "running") {
    return (
      <span className={`${LINE} text-foreground`}>
        <span title={view.title}>{view.text}</span>
        {view.stalledVerdict && (
          <span className="font-medium text-warning" title={`Plan verdict: ${view.stalledVerdict}`}>
            {" · stalled"}
          </span>
        )}
      </span>
    );
  }
  return (
    <WithTooltip label={view.title} side="top">
      <span className={`${LINE} w-fit text-muted-foreground`}>
        {view.text}
        {/* A hover tooltip never reaches a screen reader; the caveat must. */}
        <span className="sr-only"> — {view.title}</span>
      </span>
    </WithTooltip>
  );
}

/** A schedule row's next-due estimate or running state. Never blocks the editor around it. */
export function ScheduleRowStatus({ schedule }: { schedule: string }) {
  const query = useScheduleStatus();
  if (query === null) return null;
  return (
    <QueryView
      query={query}
      loading={<span className="sr-only">Loading schedule status</span>}
      error={() => <span className={`${LINE} text-muted-foreground`}>Status unavailable</span>}
    >
      {(statuses) => (
        <StatusLine
          status={statuses.find((s) => s.schedule === schedule)}
          nowMs={query.dataUpdatedAt}
        />
      )}
    </QueryView>
  );
}
